// ===========================================
// Teacher Comparison Chart Component
// ===========================================
// Bar chart comparing teacher performance metrics

import { Card, CardContent, CardHeader, Typography, Box, Divider, CircularProgress } from '@mui/material';
import { BarChart as ChartIcon } from '@mui/icons-material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

import { TeacherPerformanceRecord } from '../../api/reports.api';

// ===========================================
// TYPES
// ===========================================

interface TeacherComparisonChartProps {
  teachers: TeacherPerformanceRecord[];
  loading?: boolean;
}

// ===========================================
// CUSTOM TOOLTIP
// ===========================================

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload || !payload.length) return null;

  return (
    <Card sx={{ p: 1.5, minWidth: 200 }}>
      <Typography variant="body2" fontWeight={600} gutterBottom>
        {label}
      </Typography>
      <Divider sx={{ my: 1 }} />
      {payload.map((entry: any, index: number) => (
        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption" sx={{ color: entry.color }}>
            {entry.name}:
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            {entry.value}
          </Typography>
        </Box>
      ))}
    </Card>
  );
}

// ===========================================
// COMPONENT
// ===========================================

export default function TeacherComparisonChart({ teachers, loading }: TeacherComparisonChartProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ChartIcon color="primary" />
              <Typography variant="h6">Teacher Comparison</Typography>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (teachers.length === 0) {
    return (
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ChartIcon color="primary" />
              <Typography variant="h6">Teacher Comparison</Typography>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No teacher performance data available
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Prepare chart data - sort by lessons delivered descending, take top 10
  const topTeachers = [...teachers]
    .sort((a, b) => b.lessonsDelivered - a.lessonsDelivered)
    .slice(0, 10)
    .map((teacher) => ({
      name: teacher.teacherName.split(' ')[0], // First name only for brevity
      'Lessons Delivered': teacher.lessonsDelivered,
      'Students Taught': teacher.uniqueStudents,
    }));

  return (
    <Card>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChartIcon color="primary" />
            <Typography variant="h6">Teacher Comparison</Typography>
          </Box>
        }
        subheader={`Top ${topTeachers.length} teachers by lessons delivered`}
      />
      <Divider />
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={topTeachers}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12, fill: '#666' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#666' }}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="Lessons Delivered" fill="#4580E4" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Students Taught" fill="#FFCE00" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
